// Referrer capture — fires on landing page load (before the user is identified).
//
// Reads everything the browser knows about how the driver arrived (URL params +
// document.referrer domain), POSTs it to /referrer, and stashes the returned id
// in sessionStorage. The id is then forwarded with search + payment requests so
// the server can link the Referrer row to the user and transaction.
//
// sessionStorage is the correct lifetime: it survives same-tab navigation and
// dies on tab close — one row per visit. Note "per visit" means per visit PER
// ORIGIN: we serve four origins, so a cross-origin bounce is a fresh visit as
// far as this module is concerned. See the suppression block below.

export const REFERRER_ID_KEY = 'tcktpass_referrer_id'
const CAPTURE_FLAG_KEY = 'tcktpass_referrer_captured'

// ── SUPPRESSION: page loads that are NOT site entries ───────────────────────
//
// A Referrer row is read back at payment time as "how this driver arrived"
// (GlobalController.resolveTransactionSource reads the stored channel of the
// row whose id the browser hands over), so the newest row minted on an origin
// becomes the channel of record. Two kinds of page load must therefore never
// mint one:
//
//   1) the post-payment success page. The payment already settled; recording it
//      as an arrival is backwards. In the 90 days to 2026-10-13, 8,290 rows —
//      ~11% of the entire Referrers table — were minted on /successfulPayment/*.
//
//   2) a return redirect from a payment provider. The driver never left the
//      funnel. Stripe and Klarna are not traffic sources.
//
// WHY THIS EVER HAPPENED, since captureReferrer is already once-per-visit:
// sessionStorage is per-ORIGIN and we serve four origins (tcktpass.com plus the
// three paytickets.* skins — see lib/brand.js getBrandFromHostname). A server
// redirect that bounces the browser between them lands on an origin whose
// CAPTURE_FLAG_KEY is unset, so capture runs as though it were a first visit.
// The two live bounces are StripeController.swift:2584 (success ->
// <brand>/successfulPayment/...) and :2662 (unsettled payment -> the ticket
// scroll, stamped ?source=direct). On a SAME-origin journey the flag is already
// set and capture was always a no-op, so this guard changes nothing there.
//
// DELIBERATELY NOT SET: CAPTURE_FLAG_KEY. Suppression means "do not record THIS
// page view as an entry", not "block this tab from ever capturing". A later
// hard navigation on the same origin may still mint a row, and after the
// companion server change (Referrer.resolveChannel) that row classifies as
// `internal` — an accurate self-referral label rather than a fake channel.
// Setting the flag here would additionally blind a genuinely new journey begun
// in the same tab, which is a different and larger behaviour change.

// Exact hostnames, matched case-insensitively and in full. A named list, not a
// pattern: substring matching would let `checkout.stripe.com.evil.test` through.
export const PAYMENT_PROVIDER_HOSTS = [
    'checkout.stripe.com',
    'payments.klarna.com',
    'app.link.com',
    'dashboard.stripe.com',
]

// First path segment of the post-payment page
// (app/successfulPayment/[customerID]/[paymentID]).
const POST_PAYMENT_SEGMENT = 'successfulPayment'

// There is no [locale]/successfulPayment route today, so the segment is always
// first. Tolerating a one-segment locale prefix anyway means adding that route
// later cannot silently reopen this hole.
const LOCALE_SEGMENTS = ['en', 'es']

// True when `pathname` is the post-payment success page.
export function isPostPaymentPath(pathname) {
    if (typeof pathname !== 'string') return false
    const segments = pathname.split('/').filter(Boolean)
    if (segments.length === 0) return false
    if (segments[0] === POST_PAYMENT_SEGMENT) return true
    return LOCALE_SEGMENTS.includes(segments[0]) && segments[1] === POST_PAYMENT_SEGMENT
}

// True when `referrer` (a full document.referrer URL) came from a payment
// provider. A referrer we cannot parse is NOT a provider — unparseable means
// "no usable referrer", which the resolver already treats as direct.
export function isPaymentProviderReferrer(referrer) {
    if (!referrer) return false
    let host
    try {
        host = new URL(referrer).hostname
    } catch (error) {
        return false
    }
    if (!host) return false
    return PAYMENT_PROVIDER_HOSTS.includes(host.toLowerCase())
}

// The whole decision, pure and testable. Returns a reason string (truthy) when
// the load must not mint a row, or null when capture should proceed.
export function captureSuppressionReason(pathname, referrer) {
    if (isPostPaymentPath(pathname)) return 'post_payment_page'
    if (isPaymentProviderReferrer(referrer)) return 'payment_provider_return'
    return null
}

// Reads the stashed Referrer row id, or null if not captured yet / unavailable.
export function getReferrerID() {
    if (typeof window === 'undefined') return null
    try {
        return window.sessionStorage.getItem(REFERRER_ID_KEY) || null
    } catch (error) {
        return null
    }
}

function extractDomain(referrer) {
    if (!referrer) return null
    try {
        return new URL(referrer).hostname || null
    } catch (error) {
        return null
    }
}

// Best-effort PostHog session id for cross-referencing. Never throws.
function getPostHogSessionID() {
    try {
        if (typeof window !== 'undefined'
            && window.posthog
            && typeof window.posthog.get_session_id === 'function') {
            return window.posthog.get_session_id() || null
        }
    } catch (error) {
        // posthog not ready — fine, sessionID is optional
    }
    return null
}

// Builds the POST /referrer payload from a URL query string, referrer, and path.
export function buildReferrerPayload(searchString, referrer, pathname) {
    const params = new URLSearchParams(searchString || '')

    return {
        referringDomain: extractDomain(referrer),
        referringURL: referrer || null,
        gclid: params.get('gclid') || null,
        gbraid: params.get('gbraid') || null,
        wbraid: params.get('wbraid') || null,
        gadSource: params.get('gad_source') || null,
        utmSource: params.get('utm_source') || null,
        utmMedium: params.get('utm_medium') || null,
        utmCampaign: params.get('utm_campaign') || null,
        utmContent: params.get('utm_content') || null,
        utmTerm: params.get('utm_term') || null,
        landingPath: pathname || null,
        // existing channel tag from notification links (?source=sms/email/...)
        context: params.get('source') || null,
        sessionID: getPostHogSessionID()
    }
}

// Fire-and-forget. Idempotent per tab session: POSTs once, then reuses the stored
// id for the lifetime of the visit. Never throws — attribution must never break
// the page.
export async function captureReferrer() {
    if (typeof window === 'undefined') return null

    // Not a site entry → mint nothing. Returns whatever this origin already
    // holds (usually nothing on a cross-origin bounce), so a legitimate row
    // captured earlier in this same tab-and-origin is preserved, never replaced.
    // Checked BEFORE the flag so a suppressed load cannot set it either.
    if (captureSuppressionReason(window.location.pathname, document.referrer)) {
        return getReferrerID()
    }

    try {
        // already captured this visit → reuse the stored id
        if (window.sessionStorage.getItem(CAPTURE_FLAG_KEY)) {
            return getReferrerID()
        }
        // set the flag up front so a double mount (React StrictMode) doesn't
        // create two rows for the same visit
        window.sessionStorage.setItem(CAPTURE_FLAG_KEY, '1')
    } catch (error) {
        // sessionStorage unavailable — proceed without the idempotency guard
    }

    try {
        const payload = buildReferrerPayload(
            window.location.search,
            document.referrer,
            window.location.pathname
        )

        const response = await fetch('/api/referrer', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            cache: 'no-store'
        })

        if (!response.ok) return null

        const data = await response.json()
        const referrerID = data && data.referrerID

        if (referrerID) {
            try {
                window.sessionStorage.setItem(REFERRER_ID_KEY, referrerID)
            } catch (error) {
                // write failed — getReferrerID will just return null this visit
            }
        }

        return referrerID || null

    } catch (error) {
        console.error('captureReferrer() failed', error)
        return null
    }
}
